import { Workbox } from 'workbox-window';

/**
 * Service worker registration with fully automatic updates.
 *
 * `vite-plugin-pwa` builds the SW into `/sw.js` with `skipWaiting: true` +
 * `clientsClaim: true` (see vite.config.ts), so a freshly installed SW
 * activates and takes control immediately — no "tap to update" prompt. When
 * the new SW starts controlling this page, we reload ONCE to run the new
 * bundle. This is what makes audio/content fixes actually reach the device:
 * the old flow left new code stranded behind a waiting SW.
 *
 * Reload-loop guard: workbox-window's `controlling` event also fires on the
 * very first install (clientsClaim claims an uncontrolled page). We reload at
 * most once per page load via a module-level flag, and `controlling` only
 * fires again when a genuinely new SW takes over — so there's no loop.
 *
 * The legacy `fluent:sw-update` toast (SwUpdateToast.tsx) is now dormant —
 * with skipWaiting on, the `waiting` event no longer fires. Kept as a no-op
 * safety net in case a future build re-enables prompt-to-update.
 *
 * In dev, vite-plugin-pwa's `devOptions.enabled: false` means there's no
 * SW to register — this function is a no-op there.
 */

export function registerServiceWorker(): void {
  if (typeof window === 'undefined') return;
  if (!('serviceWorker' in navigator)) return;
  // Vite injects a global on the dev server we can detect; in prod we always run.
  if (import.meta.env.DEV) return;

  const wb = new Workbox('/sw.js');
  let reloading = false;

  // A new SW has taken control — reload once to pick up the new bundle.
  wb.addEventListener('controlling', () => {
    if (reloading) return;
    reloading = true;
    window.location.reload();
  });

  // Fallback path if a future build turns skipWaiting back off: auto-activate
  // the waiting SW instead of stranding the update behind a prompt.
  wb.addEventListener('waiting', () => {
    wb.messageSkipWaiting();
  });

  wb.register().catch((err) => {
    // Don't break the app if the SW fails to register (e.g. private mode).
    // Log silently — proper telemetry lands in Phase 13.
    if (import.meta.env.DEV) console.warn('SW register failed', err);
  });
}
